import {useMemo, useState} from 'react';
import {List, Map} from 'immutable';
import {isEqual} from 'lodash';
import {
  fieldSlug,
  collectionSlug,
} from '@packages/systems/dynamo/utils/SlugUtils';
import type {
  CollectionsType,
  ItemsType,
} from '@packages/systems/core/records/CollectionStateType';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import type {FieldType} from '@packages/systems/dynamo/records/Field';

/**
 * Return a mocked GraphQL response data object simulating page item data.
 *
 * Some queries can rely on the fields of the current page's page item.
 * For example, if a collection list has a filter "list Blog Posts whose
 * category is Category of Current Blog Post", the query must know what
 * the category of the current page item is. To do that in SSR, we need
 * to execute two queries. First query includes the Category of the page
 * item and the second one uses the category ID in the query arguments.
 *
 * However, in the Designer and Editor we have the page item information
 * in CollectionStore. So, we can optimize the rendering by skipping the
 * first query by reading, in this example, the category ID of the page
 * item from CollectionStore.
 *
 * Even though we have the items in CollectionStore in the Designer and
 * Editor, we limit the optimization to only skipping the page item query
 * for data needed for level 2 queries. We fetch everything else with the
 * same queries we use in SSR to keep the environments consistent and
 * needing to maintain only a single system shared with SSR, Designer,
 * and Editor.
 */
export function useMockPageItemData(
  collections: CollectionsType,
  items: ItemsType,
  itemId: string | null
): MockGqlItem | null {
  const next = useMemo(() => {
    const result = mockGqlItem(collections, items, itemId, 1);
    if (!result) return null;
    return {
      cms: {
        [result.gqlCollectionSlug]: {
          item: result.gqlItem,
        },
      },
    };
  }, [collections, items, itemId]);
  // use next as initial state so that current === next for the initial render
  const [current, setCurrent] = useState<MockGqlItem | null>(next);

  // as long as next and current are equal the object reference returned
  // by this hook should remain stable
  if (next === current || isEqual(next, current)) {
    return current;
  }

  // if the mockPageItemData has changed meaningfully, store it as the new
  // current value and return the next mockPageItemData
  setCurrent(next);
  return next;
}

type MockGqlItem = {[key: string]: unknown};

function mockGqlItem(
  collections: CollectionsType,
  items: ItemsType,
  itemId: string | null,
  nestingLevel: number
): null | {gqlItem: MockGqlItem; gqlCollectionSlug: string} {
  if (!itemId || nestingLevel > 2) {
    return null;
  }

  const item = items.get(itemId);
  if (!item) {
    return null;
  }

  const collection = collections.get(item.get('_cid'));
  if (!collection) {
    return null;
  }

  const collSlug = collection.slug;
  if (!collSlug) {
    return null;
  }

  const gqlCollectionSlug = collectionSlug(collSlug);

  const gqlItem = collection.fields.reduce<{[slug: string]: unknown}>(
    (mockItem, field) => {
      const gqlFieldSlug = fieldSlug(field.slug);
      mockItem[gqlFieldSlug] = getMockPageItemFieldValue(
        field,
        item,
        items,
        collections,
        nestingLevel
      );
      return mockItem;
    },
    {
      id: itemId,
      // __typename is needed by Dynamo env proxy creation
      __typename: 'CMSItem',
    }
  );

  return {gqlItem, gqlCollectionSlug};
}

function getMockPageItemFieldValue(
  field: FieldType,
  item: ItemRecordType,
  items: ItemsType,
  collections: CollectionsType,
  nestingLevel: number
): unknown {
  const {slug, type} = field;

  if (!item.has(slug)) {
    return null;
  }

  switch (type) {
    case 'PlainText':
    case 'Link':
    case 'Email':
    case 'Phone':
    case 'Date': {
      const value = item.get(slug);
      return typeof value === 'string' ? value : null;
    }
    case 'Number': {
      const value = item.get(slug);
      return typeof value === 'number' ? value : null;
    }
    case 'Bool': {
      const value = item.get(slug);
      return typeof value === 'boolean' ? value : null;
    }
    case 'Option': {
      const optionId = item.get(slug);
      const options = field.getIn(['validations', 'options']);

      if (typeof optionId === 'string' && List.isList(options)) {
        const matchingIndex = options.findIndex((opt) => {
          return (
            Map.isMap(opt) &&
            // @ts-expect-error - opt is an immutable map
            opt.get('id') === optionId
          );
        });
        const optionName = options.getIn([matchingIndex, 'name']);
        if (typeof optionName === 'string') {
          return {id: optionId, name: optionName};
        }
      }
      return null;
    }
    case 'Video': {
      const url = item.getIn([slug, 'url']);
      return typeof url === 'string' ? {url} : null;
    }
    case 'ItemRef': {
      const value = item.get(slug);
      if (typeof value !== 'string') return null;

      return (
        mockGqlItem(collections, items, value, nestingLevel + 1)?.gqlItem || {
          id: value,
        }
      );
    }
    case 'ItemRefSet': {
      const value = item.get(slug);
      return List.isList(value) ? value.toArray().map((id) => ({id})) : null;
    }
    // The following CMS field types are not used as DYN_CONTEXT filters or
    // as Collection List Props bindings, so we don't need to include them in
    // the mock item!
    // case 'Color':
    // case 'RichText':
    // case 'ImageRef':
    // case 'Set':
    // case 'FileRef':
    default: {
      return null;
    }
  }
}
